
import React, { useState } from 'react';
import { Plus, X, CreditCard, Eye, EyeOff } from 'lucide-react';
import PaymentCard from '../../../../components/serviceprovider/workerdashboard/paymentcard/PaymentCard';
import '../../../../assets/css/screens/serviceprovider/dashboard/paymentcards/PaymentCards.css';

const EMPTY_FORM = { number: '', name: '', exp: '', type: '' };

const PaymentCards = () => {
  const [cards, setCards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const formatNumber = (val) => {
    const raw = val.replace(/\D/g, '').substring(0, 16);
    return raw.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExp = (val) => {
    const raw = val.replace(/\D/g, '');
    return raw.length > 2 ? raw.substring(0, 2) + '/' + raw.substring(2, 4) : raw;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'number') formatted = formatNumber(value);
    if (name === 'exp') formatted = formatExp(value);
    setForm(prev => ({ ...prev, [name]: formatted }));
  };

  const handleSave = () => {
    const { number, name, exp, type } = form;
    if (!number || !name || !exp || !type) return;

    if (editingId !== null) {
      setCards(prev => prev.map(c => c.id === editingId ? { ...c, ...form } : c));
      setEditingId(null);
    } else {
      setCards(prev => [...prev, { id: Date.now(), ...form, locked: false }]);
    }

    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const handleEdit = (card) => {
    setForm({ number: card.number, name: card.name, exp: card.exp, type: card.type });
    setEditingId(card.id);
    setShowForm(true);
  };

  const handleToggleLock = (id) => {
    setCards(prev => prev.map(c => c.id === id ? { ...c, locked: !c.locked } : c));
  };

  const handleDelete = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
  };

  const handleClose = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  return (
    <div className="pcs-page">

      {/* Header */}
      <div className="pcs-header">
        <div>
          <h2 className="pcs-title">Payment cards</h2>
          <p className="pcs-desc">Manage your saved cards for quick and secure payments.</p>
        </div>
        <button className="pcs-add-btn" onClick={() => { setShowForm(p => !p); if (showForm) handleClose(); }}>
          {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> Add card</>}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="pcs-form">
          <p className="pcs-form-title">{editingId !== null ? 'Edit card' : 'New card'}</p>

          <div className="pcs-field">
            <label>Card number</label>
            <div className="pcs-input-wrap">
              <input
                name="number"
                value={form.number}
                onChange={handleChange}
                placeholder="•••• •••• •••• ••••"
                maxLength={19}
                type={showCardNumber ? 'text' : 'password'}
              />
              <button
                type="button"
                className="pcs-eye-btn"
                onClick={() => setShowCardNumber(p => !p)}
                aria-label={showCardNumber ? 'Hide card number' : 'Show card number'}
              >
                {showCardNumber ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div className="pcs-field">
            <label>Cardholder name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ali Hassan"
            />
          </div>

          <div className="pcs-row2">
            <div className="pcs-field">
              <label>Expiry date</label>
              <input
                name="exp"
                value={form.exp}
                onChange={handleChange}
                placeholder="MM/YY"
                maxLength={5}
              />
            </div>
            <div className="pcs-field">
              <label>Card type</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="pcs-select"
              >
                <option value="">Select card type</option>
                <option value="Visa">Visa</option>
                <option value="Mastercard">Mastercard</option>
                <option value="American Express">American Express</option>
                <option value="UnionPay">UnionPay</option>
              </select>
            </div>
          </div>

          <div className="pcs-form-actions">
            <button className="pcs-save-btn" onClick={handleSave}>Save card</button>
            <button className="pcs-cancel-btn" onClick={handleClose}>Cancel</button>
          </div>
        </div>
      )}

      {/* Cards list */}
      {cards.length === 0 ? (
        <div className="pcs-empty">
          <CreditCard size={40} />
          <p>No cards saved yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="pcs-list">
          {cards.map(card => (
            <PaymentCard
              key={card.id}
              card={card}
              onEdit={handleEdit}
              onToggleLock={handleToggleLock}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

    </div>
  );
};

export default PaymentCards;